import React, { useState, useEffect, useCallback, useRef } from 'react';
import Balloon from './Balloon';
import { API_ENDPOINTS } from '../config/api';
import { getVideoUrl, getImageUrl } from '../config/assets';
import { authenticatedFetch } from '../config/authenticatedFetch';

interface PhraseData {
  id: string;
  phrase: string;
  author: string;
  category: string;
  person_id: string;
  external_id: string;
}

interface PhrasesResponse {
  new: PhraseData[];
  old: PhraseData[];
}

interface PhraseCloudItem {
  id: string;
  text: string;
  lines: string[]; // Adicionar suporte para múltiplas linhas
  x: number;
  y: number;
  fontSize: number;
  opacity: number;
  isNew: boolean;
  showTime: number; // timestamp de quando foi mostrado
  lifeTime: number; // tempo de vida da frase (em ms)
  visible: boolean; // se está visível ou não
}

const TwoSectionLayout: React.FC = () => {
  const [balloons, setBalloons] = useState<Array<{
    id: number;
    x: number;
    y: number;
    liveTime: number;
    size: number;
    opacity: number;
  }>>([]);

  // Estado para balões da esquerda
  const [leftBalloons, setLeftBalloons] = useState<Array<{
    id: number;
    x: number;
    y: number;
    liveTime: number;
    size: number;
    opacity: number;
  }>>([]);

  // Estado para nuvem de frases
  const [phrases, setPhrases] = useState<PhraseCloudItem[]>([]);
  const [allAvailablePhrases, setAllAvailablePhrases] = useState<PhraseData[]>([]); // Pool de frases disponíveis

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);

  // Configurações para balões da DIREITA (rosa)
  const rightBalloonConfig = {
    minCount: 5,
    maxCount: 8,
    minLifeTime: 6,
    maxLifeTime: 7,
    minSize: 8,
    maxSize: 20,
    minOpacity: 15,
    maxOpacity: 25,
    spawnIntervalMin: 3000,
    spawnIntervalMax: 7000
  };

  // Configurações para balões da ESQUERDA (branco)
  const leftBalloonConfig = {
    minCount: 4,
    maxCount: 8,
    minLifeTime: 3,
    maxLifeTime: 6,
    minSize: 8,
    maxSize: 25,
    minOpacity: 100, // Sempre opacidade máxima
    maxOpacity: 100, // Sempre opacidade máxima
    spawnIntervalMin: 2000, // Reduzido de 8000ms para 2000ms
    spawnIntervalMax: 4000 // Reduzido de 15000ms para 4000ms
  };

  // Função para verificar se duas posições se sobrepõem
  const checkCollision = useCallback((pos1: {x: number, y: number}, pos2: {x: number, y: number}, minDistance: number = 80) => {
    const distance = Math.sqrt(Math.pow(pos1.x - pos2.x, 2) + Math.pow(pos1.y - pos2.y, 2));
    return distance < minDistance;
  }, []);

  // Função para gerar posição aleatória dentro do componente direito com anti-sobreposição
  const generateRandomPosition = useCallback(() => {
    const rightComponentWidth = 1512;
    const componentHeight = 1344;
    
    let attempts = 0;
    const maxAttempts = 30;
    
    while (attempts < maxAttempts) {
      const newPosition = {
        x: Math.random() * rightComponentWidth,
        y: Math.random() * componentHeight
      };
      
      // Verificar colisão com balões existentes da direita
      const hasCollision = balloons.some(balloon => 
        checkCollision(newPosition, { x: balloon.x, y: balloon.y }, 100) // Distância mínima maior para direita
      );
      
      if (!hasCollision) {
        return newPosition;
      }
      
      attempts++;
    }
    
    // Se não encontrou posição livre após várias tentativas, usar posição aleatória
    return {
      x: Math.random() * rightComponentWidth,
      y: Math.random() * componentHeight
    };
  }, [balloons, checkCollision]);

  // Função para buscar frases do endpoint
  const fetchPhrases = useCallback(async () => {
    console.log('Buscando frases do endpoint...');
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.GET_PHRASES);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Frases recebidas - New:', data.new?.length || 0, 'Old:', data.old?.length || 0);
        
        let newPhrases: PhraseData[] = [];
        let oldPhrases: PhraseData[] = [];
        
        // Verificar se a resposta tem o formato esperado {new: [], old: []}
        if (data.new && data.old) {
          newPhrases = data.new;
          oldPhrases = data.old;
        } else if (Array.isArray(data)) {
          // Se for apenas um array, tratar tudo como "old"
          oldPhrases = data;
        } else {
          console.warn('Formato de resposta não reconhecido:', data);
          return;
        }
        
        // Combinar frases new e old e atualizar o pool
        const allPhrases = [...newPhrases, ...oldPhrases];
        setAllAvailablePhrases(allPhrases);
        console.log('Pool de frases atualizado com', allPhrases.length, 'itens');
      } else {
        console.error('Erro na resposta do endpoint:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erro ao buscar frases:', error);
    }
  }, []);

  // Função para verificar e adicionar aspas duplas no texto
  const ensureQuotes = useCallback((text: string): string => {
    if (!text) return text;
    
    const trimmedText = text.trim();
    const hasStartQuote = trimmedText.startsWith('"');
    const hasEndQuote = trimmedText.endsWith('"');
    
    let result = trimmedText;
    
    // Se não tem aspas no início, adicionar
    if (!hasStartQuote) {
      result = '"' + result;
    }
    
    // Se não tem aspas no final, adicionar
    if (!hasEndQuote) {
      result = result + '"';
    }
    
    return result;
  }, []);

  // Função helper para normalizar texto para comparação
  const normalizeTextForComparison = useCallback((text: string): string => {
    const trimmed = text.trim().toLowerCase(); // Converter para minúsculas para comparação case-insensitive
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return trimmed.slice(1, -1);
    }
    return trimmed;
  }, []);

  // Função para quebrar texto em múltiplas linhas
  const breakTextIntoLines = useCallback((text: string, fontSize: number, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    // Estimar largura aproximada de cada palavra (fator muito generoso para frases longas)
    const estimateLineWidth = (line: string) => line.length * fontSize * 0.45; // Reduzido de 0.55 para 0.45 para permitir frases muito longas
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testLineWidth = estimateLineWidth(testLine);
      
      if (testLineWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Palavra muito longa, forçar quebra
          lines.push(word);
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines.length > 0 ? lines : [text];
  }, []);

  // Função para gerar posição de frase sem sobreposição com distribuição em três áreas
  const generatePhrasePosition = useCallback((existingPhrases: PhraseCloudItem[], text: string, fontSize: number) => {
    const rightComponentWidth = 1512;
    const componentHeight = 1344;
    
    // Área reservada para o título (primeira quarta parte - 336px)
    const titleAreaHeight = componentHeight / 4; // 336px
    const textAreaStart = titleAreaHeight + 50; // Começar 50px após o título
    const bottomMargin = 80; // Margem inferior aumentada
    const availableHeight = componentHeight - textAreaStart - bottomMargin; // Altura disponível
    const sideMargin = 40; // Margem lateral reduzida de 60px para 40px para dar mais espaço às frases
    const availableWidth = rightComponentWidth - (sideMargin * 2); // Largura disponível
    
    // Dividir a área da direita em três seções horizontais
    const sectionWidth = availableWidth / 3; // Cada seção tem ~464px
    const sections = [
      { name: 'left', startX: sideMargin, width: sectionWidth },
      { name: 'center', startX: sideMargin + sectionWidth, width: sectionWidth },
      { name: 'right', startX: sideMargin + (2 * sectionWidth), width: sectionWidth }
    ];
    
    // Contar frases existentes em cada seção para balanceamento
    const sectionCounts = sections.map(section => ({
      ...section,
      count: existingPhrases.filter(phrase => 
        phrase.x >= section.startX && phrase.x < section.startX + section.width
      ).length
    }));
    
    // Escolher seção com menos frases (balanceamento automático)
    const targetSection = sectionCounts.reduce((min, current) => 
      current.count < min.count ? current : min
    );
    
    // Quebrar texto em linhas se necessário - AGORA USANDO TODA A LARGURA DISPONÍVEL
    const lines = breakTextIntoLines(text, fontSize, availableWidth * 0.98); // Usar 98% de TODA a largura da área direita
    const lineHeight = fontSize * 1.3; // Aumentar espaçamento entre linhas
    const totalTextHeight = lines.length * lineHeight;
    
    // Estimar largura máxima das linhas permitindo que passem entre seções
    const maxLineWidth = Math.max(...lines.map(line => line.length * fontSize * 0.5)); // Reduzido para 0.5 para permitir frases muito longas
    
    // Sistema de grid expandido para toda a largura disponível
    const gridCols = 3; // 3 colunas para toda a área
    const gridRows = 3; // 3 linhas
    const cellWidth = availableWidth / gridCols; // Usar toda a largura disponível
    const cellHeight = availableHeight / gridRows;
    
    // Gerar posições baseadas em grid em toda a área da direita
    const gridPositions: { x: number; y: number }[] = [];
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const cellX = sideMargin + (col * cellWidth);
        const cellY = textAreaStart + (row * cellHeight);
        
        // Verificar se há espaço suficiente na célula para frases longas
        const cellSpaceWidth = cellWidth - Math.min(maxLineWidth, cellWidth * 0.9); // Permitir frases longas
        const cellSpaceHeight = cellHeight - totalTextHeight;
        
        if (cellSpaceWidth > 10 && cellSpaceHeight > 10) { // Critério mais flexível
          // Adicionar posições variadas dentro da célula
          const positions = [
            // Centro da célula
            {
              x: cellX + Math.max(cellSpaceWidth / 2, 5),
              y: cellY + Math.max(cellSpaceHeight / 2, 5)
            },
            // Início da célula (para frases longas)
            {
              x: cellX + 5,
              y: cellY + Math.max(cellSpaceHeight * 0.3, 5)
            },
            // Outras posições dentro da célula
            {
              x: cellX + Math.max(cellSpaceWidth * 0.2, 5),
              y: cellY + Math.max(cellSpaceHeight * 0.7, 5)
            }
          ];
          
          gridPositions.push(...positions);
        }
      }
    }
    
    // Embaralhar as posições do grid para distribuição mais natural
    for (let i = gridPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [gridPositions[i], gridPositions[j]] = [gridPositions[j], gridPositions[i]];
    }
    
    // Filtro mais flexível para densidade (agora que as frases podem se estender)
    const safeGridPositions = gridPositions.filter(pos => {
      // Contar frases próximas (em um raio maior)
      const nearbyPhrases = existingPhrases.filter(phrase => {
        const distance = Math.sqrt(Math.pow(pos.x - phrase.x, 2) + Math.pow(pos.y - phrase.y, 2));
        return distance < 200; // Raio de 200px
      }).length;
      
      // Permitir até 2 frases próximas (mais flexível)
      return nearbyPhrases < 2;
    });
    
    let attempts = 0;
    const maxAttempts = safeGridPositions.length + 30; // Usar posições filtradas + algumas aleatórias
    
    while (attempts < maxAttempts) {
      let newPosition: { x: number; y: number };
      
      if (attempts < safeGridPositions.length) {
        // Usar posições do grid filtradas primeiro
        newPosition = safeGridPositions[attempts];
      } else {
        // Fallback para posições aleatórias em toda a largura disponível
        const maxOffsetX = Math.max(availableWidth - maxLineWidth, 20);
        const maxOffsetY = Math.max(availableHeight - totalTextHeight, 20);
        newPosition = {
          x: sideMargin + Math.random() * maxOffsetX,
          y: textAreaStart + Math.random() * maxOffsetY
        };
      }
      
      // Garantir que a posição está dentro dos limites da área completa
      const maxAllowedX = sideMargin + availableWidth - maxLineWidth - 10;
      const maxAllowedY = textAreaStart + availableHeight - totalTextHeight;
      
      newPosition.x = Math.max(sideMargin + 10, Math.min(maxAllowedX, newPosition.x));
      newPosition.y = Math.max(textAreaStart, Math.min(maxAllowedY, newPosition.y));
      
      // Verificação adicional: se a frase ainda vaza da área total, redimensionar
      if (newPosition.x + maxLineWidth > sideMargin + availableWidth - 10) {
        newPosition.x = sideMargin + availableWidth - maxLineWidth - 10;
      }
      if (newPosition.y + totalTextHeight > textAreaStart + availableHeight) {
        newPosition.y = textAreaStart + availableHeight - totalTextHeight;
      }
      
      // Se ainda não cabe na área total, pular esta tentativa
      if (newPosition.x < sideMargin || newPosition.y < textAreaStart) {
        attempts++;
        continue;
      }
      
      // Verificar colisão com frases existentes - VERSÃO ULTRA MELHORADA
      const minSafetyMargin = 40; // Margem reduzida de 50px para 40px para permitir frases mais longas
      const hasCollision = existingPhrases.some(phrase => {
        // Calcular dimensões da frase existente com precisão e margem extra
        const phraseMaxWidth = Math.max(...phrase.lines.map(line => line.length * phrase.fontSize * 0.5));
        const phraseHeight = phrase.lines.length * phrase.fontSize * 1.3;
        
        // Calcular dimensões da nova frase
        const newPhraseWidth = maxLineWidth;
        const newPhraseHeight = totalTextHeight;
        
        // Áreas expandidas com margem de segurança aumentada
        const existingPhraseArea = {
          left: phrase.x - minSafetyMargin,
          right: phrase.x + phraseMaxWidth + minSafetyMargin,
          top: phrase.y - minSafetyMargin,
          bottom: phrase.y + phraseHeight + minSafetyMargin
        };
        
        const newPhraseArea = {
          left: newPosition.x,
          right: newPosition.x + newPhraseWidth,
          top: newPosition.y,
          bottom: newPosition.y + newPhraseHeight
        };
        
        // Verificar sobreposição com método mais robusto
        const horizontalOverlap = !(
          newPhraseArea.right < existingPhraseArea.left ||
          newPhraseArea.left > existingPhraseArea.right
        );
        
        const verticalOverlap = !(
          newPhraseArea.bottom < existingPhraseArea.top ||
          newPhraseArea.top > existingPhraseArea.bottom
        );
        
        // Há colisão se há sobreposição tanto horizontal quanto vertical
        return horizontalOverlap && verticalOverlap;
      });
      
      if (!hasCollision) {
        return { position: newPosition, lines, section: 'full-width' };
      }
      
      attempts++;
    }
    
    // Fallback melhorado: usar toda a largura disponível
    console.warn('Não foi possível encontrar posição livre para frase, usando fallback em toda a área');
    
    // Garantir que as dimensões estão corretas para o fallback
    const safeMaxX = sideMargin + availableWidth - maxLineWidth - 10;
    const safeMaxY = textAreaStart + availableHeight - totalTextHeight;
    
    // Posição aleatória em toda a área da direita
    let bestPosition = {
      x: sideMargin + 10 + Math.random() * Math.max(safeMaxX - sideMargin - 10, 50),
      y: textAreaStart + Math.random() * Math.max(safeMaxY - textAreaStart, 50)
    };
    
    // Verificação final do fallback para toda a área
    bestPosition.x = Math.max(sideMargin + 10, Math.min(safeMaxX, bestPosition.x));
    bestPosition.y = Math.max(textAreaStart, Math.min(safeMaxY, bestPosition.y));
    
    return {
      position: bestPosition,
      lines,
      section: 'full-width' // Indica que está usando toda a largura
    };
  }, [breakTextIntoLines]);

  // Função para verificar e remover frases sobrepostas (com proteção de mínimo)
  const cleanupOverlappingPhrases = useCallback((phrasesToCheck: PhraseCloudItem[]): PhraseCloudItem[] => {
    // Se há poucas frases, ser menos rigoroso para manter o mínimo
    if (phrasesToCheck.length <= 5) {
      console.log('Poucas frases detectadas - aplicando limpeza menos rigorosa');
      return phrasesToCheck; // Não remover nenhuma se há 5 ou menos
    }
    
    const cleanPhrases: PhraseCloudItem[] = [];
    const minSafetyMargin = 50;
    
    for (const phrase of phrasesToCheck) {
      const phraseMaxWidth = Math.max(...phrase.lines.map(line => line.length * phrase.fontSize * 0.5));
      const phraseHeight = phrase.lines.length * phrase.fontSize * 1.3;
      
      const hasOverlap = cleanPhrases.some(existingPhrase => {
        const existingMaxWidth = Math.max(...existingPhrase.lines.map(line => line.length * existingPhrase.fontSize * 0.5));
        const existingHeight = existingPhrase.lines.length * existingPhrase.fontSize * 1.3;
        
        const phraseArea = {
          left: phrase.x - minSafetyMargin,
          right: phrase.x + phraseMaxWidth + minSafetyMargin,
          top: phrase.y - minSafetyMargin,
          bottom: phrase.y + phraseHeight + minSafetyMargin
        };
        
        const existingArea = {
          left: existingPhrase.x,
          right: existingPhrase.x + existingMaxWidth,
          top: existingPhrase.y,
          bottom: existingPhrase.y + existingHeight
        };
        
        const horizontalOverlap = !(
          phraseArea.right < existingArea.left ||
          phraseArea.left > existingArea.right
        );
        
        const verticalOverlap = !(
          phraseArea.bottom < existingArea.top ||
          phraseArea.top > existingArea.bottom
        );
        
        return horizontalOverlap && verticalOverlap;
      });
      
      if (!hasOverlap) {
        cleanPhrases.push(phrase);
      } else {
        console.warn('Frase removida por sobreposição:', phrase.text.substring(0, 30) + '...');
      }
    }
    
    // GARANTIR MÍNIMO: Se após limpeza ficou com menos de 3, adicionar algumas de volta
    if (cleanPhrases.length < 3 && phrasesToCheck.length >= 3) {
      console.log('Limpeza removeu muitas frases - adicionando algumas de volta para manter mínimo');
      const removedPhrases = phrasesToCheck.filter(phrase => !cleanPhrases.includes(phrase));
      const toAddBack = removedPhrases
        .sort((a, b) => b.showTime - a.showTime)
        .slice(0, 3 - cleanPhrases.length);
      cleanPhrases.push(...toAddBack);
    }
    
    return cleanPhrases;
  }, []);

  // Função para adicionar uma nova frase individual
  const addRandomPhrase = useCallback(() => {
    if (allAvailablePhrases.length === 0) return;
    
    setPhrases(currentPhrases => {
      // Obter textos normalizados das frases já na tela para comparação
      const currentNormalizedTexts = currentPhrases.map(p => normalizeTextForComparison(p.text));
      
      // Filtrar frases disponíveis que não estão sendo exibidas
      const availableForDisplay = allAvailablePhrases.filter(phrase => {
        const normalizedPhrase = normalizeTextForComparison(phrase.phrase);
        
        // Verificar se a frase normalizada já está na tela
        const isAlreadyDisplayed = currentNormalizedTexts.includes(normalizedPhrase);
        
        return !isAlreadyDisplayed;
      });
      
      // Se não há frases disponíveis para exibir, retornar sem adicionar
      if (availableForDisplay.length === 0) {
        console.log('Nenhuma frase nova disponível para exibir. Frases atuais:', currentPhrases.length);
        return currentPhrases;
      }
      
      // Escolher uma frase aleatória das disponíveis
      const randomPhrase = availableForDisplay[Math.floor(Math.random() * availableForDisplay.length)];
      
      // Aplicar aspas duplas na frase se necessário
      const phraseWithQuotes = ensureQuotes(randomPhrase.phrase);
      
      const baseFontSize = 32 + Math.random() * 38; // Tamanho entre 32px e 70px (aumentado de 30-70)
      const fontSize = baseFontSize; // Por agora, sem distinção de "new"
      
      // Gerar posição sem sobreposição e quebrar texto se necessário
      const result = generatePhrasePosition(currentPhrases, phraseWithQuotes, fontSize);
      
      const phraseItem: PhraseCloudItem = {
        id: randomPhrase.id + '_' + Date.now(), // ID único para permitir repetições futuras
        text: phraseWithQuotes, // Usar o texto com aspas
        lines: result.lines, // Adicionar as linhas quebradas
        x: result.position.x,
        y: result.position.y,
        fontSize,
        opacity: 0.7 + Math.random() * 0.3,
        isNew: false,
        showTime: Date.now(),
        lifeTime: 8000 + Math.random() * 12000, // Vida entre 8-20 segundos
        visible: true
      };
      
      console.log('Nova frase adicionada na seção:', result.section, '- Texto:', phraseWithQuotes.substring(0, 30) + '...');
      
      // Aplicar limpeza de sobreposições antes de retornar
      const updatedPhrases = [...currentPhrases, phraseItem];
      return cleanupOverlappingPhrases(updatedPhrases);
    });
  }, [allAvailablePhrases, generatePhrasePosition, ensureQuotes, cleanupOverlappingPhrases]);

  // Função para gerar posição aleatória dentro do componente esquerdo
  const generateLeftRandomPosition = useCallback(() => {
    const leftComponentWidth = 672;
    const componentHeight = 1344;
    
    // Limitar ao terço inferior da tela (últimos 448px de altura)
    const lowerThirdStart = componentHeight * (2/3); // 896px
    const lowerThirdHeight = componentHeight - lowerThirdStart; // 448px
    
    return {
      x: Math.random() * leftComponentWidth,
      y: lowerThirdStart + Math.random() * lowerThirdHeight
    };
  }, []);

  // Função melhorada para gerar posição sem sobreposição para balões da esquerda
  const generateLeftPositionWithoutOverlap = useCallback(() => {
    const leftComponentWidth = 672;
    const componentHeight = 1344;
    const lowerThirdStart = componentHeight * (2/3); // 896px
    const lowerThirdHeight = componentHeight - lowerThirdStart; // 448px
    
    let attempts = 0;
    const maxAttempts = 20;
    
    while (attempts < maxAttempts) {
      const newPosition = {
        x: Math.random() * leftComponentWidth,
        y: lowerThirdStart + Math.random() * lowerThirdHeight
      };
      
      // Verificar colisão com balões existentes
      const hasCollision = leftBalloons.some(balloon => 
        checkCollision(newPosition, { x: balloon.x, y: balloon.y })
      );
      
      if (!hasCollision) {
        return newPosition;
      }
      
      attempts++;
    }
    
    // Se não encontrou posição livre após várias tentativas, usar posição aleatória
    return {
      x: Math.random() * leftComponentWidth,
      y: lowerThirdStart + Math.random() * lowerThirdHeight
    };
  }, [leftBalloons, checkCollision]);

  // Função para calcular tamanho com distribuição ponderada para balões da esquerda
  const generateLeftBalloonSize = useCallback(() => {
    const { minSize, maxSize } = leftBalloonConfig;
    const totalRange = maxSize - minSize;
    
    // Dividir em 3 terços: menor, meio, maior
    const firstThird = minSize + (totalRange / 3); // 8 + 5.67 = ~13.67
    const secondThird = minSize + (2 * totalRange / 3); // 8 + 11.33 = ~19.33
    // Terceiro seria de 19.33 até 25
    
    const random = Math.random();
    
    if (random <= 0.9) {
      // 90% chance para terço menor (8 até ~13.67)
      return minSize + Math.random() * (firstThird - minSize);
    } else {
      // 10% chance para terço maior (~19.33 até 25) - pulando o meio
      return secondThird + Math.random() * (maxSize - secondThird);
    }
  }, [leftBalloonConfig]);

  // Função para criar um novo balão com propriedades aleatórias (DIREITA)
  const createBalloon = useCallback(() => {
    console.log('createBalloon chamado'); // Debug log
    const position = generateRandomPosition();
    const liveTime = rightBalloonConfig.minLifeTime + 
      Math.random() * (rightBalloonConfig.maxLifeTime - rightBalloonConfig.minLifeTime);
    const size = rightBalloonConfig.minSize + 
      Math.random() * (rightBalloonConfig.maxSize - rightBalloonConfig.minSize);
    const opacity = rightBalloonConfig.minOpacity + 
      Math.random() * (rightBalloonConfig.maxOpacity - rightBalloonConfig.minOpacity);

    const balloon = {
      id: Date.now() + Math.random(),
      x: position.x,
      y: position.y,
      liveTime,
      size,
      opacity
    };
    
    console.log('Balão criado:', balloon); // Debug log
    return balloon;
  }, [generateRandomPosition, rightBalloonConfig]);

  // Função para remover um balão (direita)
  const removeBalloon = useCallback((id: number) => {
    setBalloons(prev => prev.filter(balloon => balloon.id !== id));
  }, []);

  // Função para remover um balão (esquerda)
  const removeLeftBalloon = useCallback((id: number) => {
    setLeftBalloons(prev => prev.filter(balloon => balloon.id !== id));
  }, []);

  // Função para adicionar novos balões dinamicamente (DIREITA)
  const addBalloonIfNeeded = useCallback(() => {
    if (!isActiveRef.current) return;

    setBalloons(prev => {
      if (prev.length < rightBalloonConfig.maxCount) {
        return [...prev, createBalloon()];
      }
      return prev;
    });
  }, [createBalloon, rightBalloonConfig.maxCount]);

    // Sistema de spawn dinâmico - VERSÃO SIMPLIFICADA (Direita)
  const scheduleNextBalloon = useCallback(() => {
    if (!isActiveRef.current) return;

    const nextSpawnTime = rightBalloonConfig.spawnIntervalMin + 
      Math.random() * (rightBalloonConfig.spawnIntervalMax - rightBalloonConfig.spawnIntervalMin);

    intervalRef.current = setTimeout(() => {
      console.log('Tentando adicionar novo balão (direita)...'); // Debug log
      
      setBalloons(prev => {
        console.log('Balões atuais (direita):', prev.length, '/ Max:', rightBalloonConfig.maxCount); // Debug log
        if (prev.length < rightBalloonConfig.maxCount) {
          const position = generateRandomPosition();
          const newBalloon = {
            id: Date.now() + Math.random() * 1000,
            x: position.x,
            y: position.y,
            liveTime: rightBalloonConfig.minLifeTime + Math.random() * (rightBalloonConfig.maxLifeTime - rightBalloonConfig.minLifeTime),
            size: rightBalloonConfig.minSize + Math.random() * (rightBalloonConfig.maxSize - rightBalloonConfig.minSize),
            opacity: rightBalloonConfig.minOpacity + Math.random() * (rightBalloonConfig.maxOpacity - rightBalloonConfig.minOpacity)
          };
          console.log('Novo balão adicionado (direita):', newBalloon); // Debug log
          scheduleNextBalloon(); // Reagendar próximo balão
          return [...prev, newBalloon];
        }
        scheduleNextBalloon(); // Reagendar mesmo se não adicionou
        return prev;
      });
    }, nextSpawnTime);
  }, [generateRandomPosition, rightBalloonConfig]);

  // Sistema de spawn dinâmico para balões da esquerda
  const scheduleNextLeftBalloon = useCallback(() => {
    if (!isActiveRef.current) return;

    const nextSpawnTime = leftBalloonConfig.spawnIntervalMin + 
      Math.random() * (leftBalloonConfig.spawnIntervalMax - leftBalloonConfig.spawnIntervalMin);

    setTimeout(() => {
      console.log('Tentando adicionar novo balão (esquerda)...'); // Debug log
      
      setLeftBalloons(prev => {
        console.log('Balões atuais (esquerda):', prev.length, '/ Max:', leftBalloonConfig.maxCount); // Debug log
        if (prev.length < leftBalloonConfig.maxCount) {
          const position = generateLeftPositionWithoutOverlap();
          const newBalloon = {
            id: Date.now() + Math.random() * 1000 + 5000, // ID diferente para evitar conflitos
            x: position.x,
            y: position.y,
            liveTime: leftBalloonConfig.minLifeTime + Math.random() * (leftBalloonConfig.maxLifeTime - leftBalloonConfig.minLifeTime),
            size: generateLeftBalloonSize(), // Usando a nova função de distribuição ponderada
            opacity: leftBalloonConfig.minOpacity + Math.random() * (leftBalloonConfig.maxOpacity - leftBalloonConfig.minOpacity)
          };
          console.log('Novo balão adicionado (esquerda):', newBalloon); // Debug log
          scheduleNextLeftBalloon(); // Reagendar próximo balão
          return [...prev, newBalloon];
        }
        scheduleNextLeftBalloon(); // Reagendar mesmo se não adicionou
        return prev;
      });
    }, nextSpawnTime);
  }, [generateLeftRandomPosition, leftBalloonConfig]);

  // Inicialização e limpeza - VERSÃO FUNCIONAL (DIREITA)
  useEffect(() => {
    console.log('useEffect de inicialização executado'); // Debug log
    isActiveRef.current = true;

    // Criar balões iniciais usando a função createBalloon
    const initialCount = rightBalloonConfig.minCount + 
      Math.floor(Math.random() * (rightBalloonConfig.maxCount - rightBalloonConfig.minCount + 1));
    console.log('Criando balões iniciais:', initialCount); // Debug log
    
    const initialBalloons = [];
    for(let i = 0; i < initialCount; i++) {
      const position = generateRandomPosition();
      const balloon = {
        id: Date.now() + Math.random() * 1000,
        x: position.x,
        y: position.y,
        liveTime: rightBalloonConfig.minLifeTime + Math.random() * (rightBalloonConfig.maxLifeTime - rightBalloonConfig.minLifeTime),
        size: rightBalloonConfig.minSize + Math.random() * (rightBalloonConfig.maxSize - rightBalloonConfig.minSize),
        opacity: rightBalloonConfig.minOpacity + Math.random() * (rightBalloonConfig.maxOpacity - rightBalloonConfig.minOpacity)
      };
      initialBalloons.push(balloon);
    }
    
    console.log('Balões criados:', initialBalloons); // Debug log
    setBalloons(initialBalloons);

    // Iniciar sistema de spawn dinâmico
    scheduleNextBalloon();

    return () => {
      console.log('Cleanup do useEffect'); // Debug log
      isActiveRef.current = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []); // Dependencies simplificadas

  // useEffect para polling de frases a cada 10 segundos
  useEffect(() => {
    // Buscar frases imediatamente
    fetchPhrases();
    
    // Configurar polling a cada 10 segundos
    const phrasesInterval = setInterval(fetchPhrases, 10000);
    
    return () => clearInterval(phrasesInterval);
  }, [fetchPhrases]);

  // useEffect para gerenciar a vida das frases individuais
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      
      setPhrases(prevPhrases => {
        // Remover frases que expiraram - MAS GARANTIR MÍNIMO
        const activePhrases = prevPhrases.filter(phrase => {
          const ageInMs = now - phrase.showTime;
          return ageInMs < phrase.lifeTime;
        });
        
        // PROTEÇÃO: Se ficar com menos de 3 frases ativas, manter algumas expiradas
        let phrasesToKeep = activePhrases;
        if (activePhrases.length < 3) {
          console.log('Protegendo mínimo de frases - mantendo algumas expiradas');
          const expiredPhrases = prevPhrases.filter(phrase => {
            const ageInMs = now - phrase.showTime;
            return ageInMs >= phrase.lifeTime;
          });
          
          // Manter as mais recentes das expiradas para garantir mínimo de 5
          const toKeep = expiredPhrases
            .sort((a, b) => b.showTime - a.showTime)
            .slice(0, Math.max(5 - activePhrases.length, 0));
          
          phrasesToKeep = [...activePhrases, ...toKeep];
        }
        
        // Remover duplicatas - verificar se há frases com o mesmo conteúdo
        const uniquePhrases = phrasesToKeep.filter((phrase, index, array) => {
          const currentNormalized = normalizeTextForComparison(phrase.text);
          
          // Encontrar a primeira ocorrência desta frase
          const firstIndex = array.findIndex(p => 
            normalizeTextForComparison(p.text) === currentNormalized
          );
          
          // Manter apenas a primeira ocorrência (mais antiga)
          return firstIndex === index;
        });
        
        // Log se removeu duplicatas - mas manter mínimo
        if (uniquePhrases.length !== phrasesToKeep.length) {
          console.log(`Removidas ${phrasesToKeep.length - uniquePhrases.length} frases duplicadas`);
          
          // Se após remover duplicatas ficou abaixo do mínimo, manter algumas duplicatas
          if (uniquePhrases.length < 3) {
            console.log('Mantendo algumas duplicatas para garantir mínimo');
            const removedDuplicates = phrasesToKeep.filter(phrase => !uniquePhrases.includes(phrase));
            const toAddBack = removedDuplicates
              .sort((a, b) => b.showTime - a.showTime)
              .slice(0, 3 - uniquePhrases.length);
            uniquePhrases.push(...toAddBack);
          }
        }
        
        // Reduzir tamanho das frases novas após 2 segundos
        const updatedPhrases = uniquePhrases.map(phrase => {
          if (phrase.isNew && now - phrase.showTime > 2000) {
            const normalSize = phrase.fontSize / 1.8;
            return {
              ...phrase,
              fontSize: normalSize,
              isNew: false
            };
          }
          return phrase;
        });
        
        // Aplicar limpeza de sobreposições antes de retornar
        return cleanupOverlappingPhrases(updatedPhrases);
      });
    }, 500); // Verificar a cada 500ms
    
    return () => clearInterval(interval);
  }, []);

  // useEffect para inicializar com várias frases no início
  useEffect(() => {
    if (allAvailablePhrases.length > 0 && phrases.length === 0) {
      console.log('Inicializando com frases iniciais');
      // GARANTIR sempre entre 5-8 frases iniciais (mínimo de 5)
      const initialCount = 5 + Math.floor(Math.random() * 4);
      
      // Embaralhar frases para seleção aleatória inicial
      const shuffledPhrases = [...allAvailablePhrases].sort(() => Math.random() - 0.5);
      const selectedPhrases = shuffledPhrases.slice(0, Math.min(initialCount, allAvailablePhrases.length));
      
      selectedPhrases.forEach((_, i) => {
        setTimeout(() => addRandomPhrase(), i * 300); // Delay reduzido para 300ms para carregar mais rápido
      });
    }
  }, [allAvailablePhrases.length, phrases.length, addRandomPhrase]);

  // useEffect para adicionar novas frases periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      // GARANTIR MÍNIMO: sempre adicionar se há menos de 3 frases
      const currentVisiblePhrases = phrases.filter(p => p.visible).length;
      const shouldAddForMinimum = currentVisiblePhrases < 3;
      const shouldAddForNormal = allAvailablePhrases.length > 0 && phrases.length < 12;
      
      if (shouldAddForMinimum || shouldAddForNormal) {
        addRandomPhrase();
      }
    }, 2000 + Math.random() * 3000); // A cada 2-5 segundos (ainda mais espaçado)
    
    return () => clearInterval(interval);
  }, [allAvailablePhrases, phrases.length, addRandomPhrase]);

  // useEffect adicional para GARANTIR mínimo de frases sempre
  useEffect(() => {
    const checkMinimumInterval = setInterval(() => {
      if (allAvailablePhrases.length > 0) {
        const currentVisiblePhrases = phrases.filter(p => p.visible).length;
        
        // Se há menos de 3 frases visíveis, adicionar imediatamente
        if (currentVisiblePhrases < 3) {
          console.log(`Detectado apenas ${currentVisiblePhrases} frases visíveis. Adicionando frases para garantir mínimo.`);
          
          // Adicionar quantas frases forem necessárias para chegar ao mínimo de 5
          const neededPhrases = 5 - currentVisiblePhrases;
          for (let i = 0; i < neededPhrases; i++) {
            setTimeout(() => addRandomPhrase(), i * 200); // Delay mínimo entre adições
          }
        }
      }
    }, 2000); // Verificar a cada 2 segundos
    
    return () => clearInterval(checkMinimumInterval);
  }, [allAvailablePhrases.length, phrases, addRandomPhrase]);

  // useEffect para inicializar balões da esquerda
  useEffect(() => {
    console.log('useEffect de inicialização da esquerda executado'); // Debug log
    
    // Criar balões iniciais da esquerda
    const initialCount = leftBalloonConfig.minCount + 
      Math.floor(Math.random() * (leftBalloonConfig.maxCount - leftBalloonConfig.minCount + 1));
    console.log('Criando balões iniciais (esquerda):', initialCount); // Debug log
    
    const initialLeftBalloons: Array<{
      id: number;
      x: number;
      y: number;
      liveTime: number;
      size: number;
      opacity: number;
    }> = [];
    
    for(let i = 0; i < initialCount; i++) {
      const leftComponentWidth = 672;
      const componentHeight = 1344;
      const lowerThirdStart = componentHeight * (2/3);
      const lowerThirdHeight = componentHeight - lowerThirdStart;
      
      let position: { x: number; y: number };
      let attempts = 0;
      const maxAttempts = 20;
      
      // Tentar encontrar posição sem sobreposição com balões já criados
      do {
        position = {
          x: Math.random() * leftComponentWidth,
          y: lowerThirdStart + Math.random() * lowerThirdHeight
        };
        
        attempts++;
      } while (
        attempts < maxAttempts && 
        initialLeftBalloons.some(existing => 
          checkCollision(position, { x: existing.x, y: existing.y })
        )
      );
      
      const balloon = {
        id: Date.now() + Math.random() * 1000 + 10000, // ID diferente para evitar conflitos
        x: position.x,
        y: position.y,
        liveTime: leftBalloonConfig.minLifeTime + Math.random() * (leftBalloonConfig.maxLifeTime - leftBalloonConfig.minLifeTime),
        size: generateLeftBalloonSize(), // Usando a nova função de distribuição ponderada
        opacity: leftBalloonConfig.minOpacity + Math.random() * (leftBalloonConfig.maxOpacity - leftBalloonConfig.minOpacity)
      };
      initialLeftBalloons.push(balloon);
    }
    
    console.log('Balões da esquerda criados:', initialLeftBalloons); // Debug log
    setLeftBalloons(initialLeftBalloons);

    // Iniciar sistema de spawn dinâmico da esquerda
    scheduleNextLeftBalloon();

  }, []); // Dependencies simplificadas

  // Limpeza periódica de balões (similar ao PhotoWall)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (!isActiveRef.current) return;
      
      setBalloons(prev => {
        // Manter apenas balões ativos e não exceder o máximo
        return prev.slice(0, rightBalloonConfig.maxCount);
      });
    }, 10000); // Limpeza a cada 10 segundos

    return () => clearInterval(cleanupInterval);
  }, [rightBalloonConfig.maxCount]);

  return (
    <div 
      style={{
        width: "2184px",
        height: "1344px",
        backgroundColor: "#f0f0f0",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        fontFamily: "'Poppins', sans-serif",
        border: "2px solid #333",
        position: "relative"
      }}
    >
      {/* Componente da Esquerda - 672px x 1344px */}
      <div 
        style={{
          width: "672px",
          height: "1344px",
          backgroundColor: "white",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <video
          src={getVideoUrl('parte_branca_ajuste.mp4')}
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            border: "none",
            outline: "none",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
        />
      </div>
      
      {/* Componente da Direita - 1512px x 1344px */}
      <div 
        style={{
          width: "1512px",
          height: "1344px",
          backgroundColor: "#cd0c62",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Vídeo de fundo para o componente da direita */}
        <video
          src={getVideoUrl('fundo_ajuste.mp4')}
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            border: "none",
            outline: "none",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1
          }}
        />
        
        {/* Debug: mostrar contagem */}
        {/* <div style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: "white",
          fontSize: "18px",
          zIndex: 1000,
          backgroundColor: "rgba(0,0,0,0.7)",
          padding: "10px",
          borderRadius: "5px"
        }}>
          <div>Direita: {balloons.length}/{rightBalloonConfig.maxCount}</div>
          <div>Esquerda: {leftBalloons.length}/{leftBalloonConfig.maxCount}</div>
          <div>Frases: {phrases.length}</div>
        </div> */}
        
        {/* Frase centralizada vertical e horizontalmente */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "1512px",
            height: "448px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            pointerEvents: "none"
          }}
        >
          <span
            style={{
              color: "white",
              fontFamily: "ACTIVE-GROTESK, sans-serif, bold",
              fontSize: 110,
              // fontWeight: 1000,
              // letterSpacing: "0.01em",
              textAlign: "center",
              whiteSpace: "nowrap"
            }}
          >
            Levar mais vida é...
          </span>
        </div>

        {/* Nuvem de frases */}
        {phrases.map((phrase) => {
          // Validação adicional para evitar vazamento visual com novos parâmetros
          const maxLineWidth = Math.max(...phrase.lines.map(line => line.length * phrase.fontSize * 0.5));
          const totalHeight = phrase.lines.length * phrase.fontSize * 1.3;
          const titleAreaHeight = 1344 / 4; // 336px
          const textAreaStart = titleAreaHeight + 50; // 386px
          const sideMargin = 40; // Atualizado para 40px
          const bottomMargin = 80;
          
          const isWithinBounds = 
            phrase.x >= sideMargin && 
            phrase.x + maxLineWidth <= 1512 - sideMargin && 
            phrase.y >= textAreaStart && 
            phrase.y + totalHeight <= 1344 - bottomMargin;
          
          if (!isWithinBounds) {
            console.warn('Frase fora dos limites detectada:', phrase.id, phrase.x, phrase.y);
            return null; // Não renderizar frases fora dos limites
          }
          
          return (
            <div
              key={phrase.id}
              style={{
                position: "absolute",
                left: phrase.x,
                top: phrase.y,
                color: "white",
                fontFamily: "BASKVILL, serif",
                fontSize: phrase.fontSize,
                opacity: phrase.visible ? phrase.opacity : 0,
                zIndex: 500,
                pointerEvents: "none",
                transition: "font-size 0.5s ease-out, opacity 0.8s ease-in-out", // Transições suaves
                fontWeight: phrase.isNew ? "bold" : "normal", // Destacar frases novas
                maxWidth: `${1512 - 80}px`, // Limitar largura máxima
                wordWrap: "break-word", // Quebrar palavras se necessário
                overflow: "hidden" // Esconder qualquer overflow
              }}
            >
              {phrase.lines.map((line, lineIndex) => (
                <div
                  key={lineIndex}
                  style={{
                    whiteSpace: "nowrap",
                    lineHeight: 1.3, // Aumentado de 1.2 para 1.3 para corresponder ao cálculo
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {line}
                </div>
              ))}
            </div>
          );
        })}

        {/* Balões flutuantes APENAS dentro do componente da direita */}
        {balloons.map((balloon, index) => (
          <Balloon
            key={balloon.id}
            minSize={balloon.size}
            maxSize={balloon.size}
            maxOpacity={balloon.opacity}
            liveTime={balloon.liveTime}
            position={{ x: balloon.x, y: balloon.y }}
            imageSrc={getImageUrl('balão_ache_01.png')}
            onDestroy={() => removeBalloon(balloon.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default TwoSectionLayout;
